import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../auth/service';
import { Simulado, SiteConfig, Question } from '../types';
import QuestionImportPanel from './QuestionImportPanel';
import QuestionEditModal from './QuestionEditModal';
import ConfirmationModal from './ConfirmationModal';

interface Props {
  currentConfig: SiteConfig;
  onConfigChange: (cfg: SiteConfig) => void;
}

const emptyForm: Simulado = {
  code: '',
  name: '',
  passingScore: 720,
  examDurationSeconds: 7800,
  subtitle: '',
  questionIds: [],
  domains: [],
  questionCount: 65
};

const SimuladoManagement: React.FC<Props> = ({ currentConfig, onConfigChange }) => {
  const [simulados, setSimulados] = useState<Simulado[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Simulado>({ ...emptyForm });
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [pageTitle, setPageTitle] = useState(currentConfig.pageTitle || '');
  const [mainTitle, setMainTitle] = useState(currentConfig.mainTitle || '');
  const [siteUrl, setSiteUrl] = useState(currentConfig.siteUrl || '');
  const [siteLogoUrl, setSiteLogoUrl] = useState(currentConfig.siteLogoUrl || '');
  const [subtitle, setSubtitle] = useState(currentConfig.subtitle || '');
  const [logoUrl, setLogoUrl] = useState(currentConfig.logoUrl || ''); 
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Estados para CRUD de questões dentro do SimuladoManagement
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [questionForm, setQuestionForm] = useState<Partial<Question>>({});

  const loadSimulados = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiClient.get('/admin/simulados');
      setSimulados(Array.isArray(list) ? list : []);
    } catch (err: any) {
      setError(err.message || 'Falha ao carregar simulados');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQuestions = async (code: string) => {
    if (!code) return;
    setLoadingQuestions(true);
    try {
      const q = await apiClient.get('/admin/questions', { headers: { 'x-simulado': code } });
      const data = Array.isArray(q) ? q : (q?.data || q?.questions || []);
      setQuestions([...data].sort((a, b) => a.id - b.id));
    } catch {
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleSaveQuestion = async () => {
    try {
      const code = form.code;
      if (!code) return;
      const opts = { headers: { 'x-simulado': code } };
      const { _id, __v, ...payload } = questionForm as any;

      if (editingQuestion) {
        await apiClient.put(`/admin/questions/${editingQuestion.id}`, payload, opts);
      } else {
        const nextId = questions.length > 0 ? Math.max(...questions.map(q => q.id)) + 1 : 1;
        await apiClient.post('/admin/questions', { ...payload, id: nextId }, opts);
      }
      setShowQuestionModal(false);
      setEditingQuestion(null);
      setQuestionForm({});
      loadQuestions(code);
      alert('Questão salva com sucesso!');
    } catch (err: any) {
      alert(err.message || 'Falha ao salvar questão');
    }
  };

  const handleDeleteQuestion = async () => {
    if (!editingQuestion || !form.code) return;
    try {
      await apiClient.delete(`/admin/questions/${editingQuestion.id}`, { headers: { 'x-simulado': form.code } });
      setShowDeleteModal(false);
      setEditingQuestion(null);
      loadQuestions(form.code);
    } catch (err: any) {
      alert(err.message || 'Falha ao excluir questão');
    }
  };

  const handleQuestionFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('explanation.')) {
      const [_, subField] = name.split('.');
      setQuestionForm(prev => ({
        ...prev,
        explanation: {
          ...(prev.explanation || { general: '', options: {} }),
          [subField]: value
        }
      }));
    } else {
      setQuestionForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleOptionsChange = (key: string, value: string) => {
    setQuestionForm(prev => ({
      ...prev,
      options: { ...(prev.options || {}), [key]: value }
    }));
  };

  const handleExplanationOptionsChange = (key: string, value: string) => {
    setQuestionForm(prev => ({
      ...prev,
      explanation: {
        ...(prev.explanation || { general: '', options: {} }),
        options: { ...(prev.explanation?.options || {}), [key]: value }
      }
    }));
  };

  const handleCorrectAnswersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setQuestionForm(prev => {
      const currentCorrect = new Set(prev.correctAnswers || []);
      if (checked) {
        currentCorrect.add(value);
      } else {
        currentCorrect.delete(value);
      }
      return { ...prev, correctAnswers: Array.from(currentCorrect) };
    });
  };

  useEffect(() => { 
    loadSimulados();
  }, [loadSimulados]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const num = Number(value);
    if (name === 'examDurationMinutes') {
      setForm(prev => ({ ...prev, examDurationSeconds: isNaN(num) ? 0 : num * 60 }));
      return;
    }
    if (name === 'trialLimitMinutes') {
      setForm(prev => ({ ...prev, trialLimitSeconds: isNaN(num) ? 0 : num * 60 }));
      return;
    }
    setForm(prev => ({
      ...prev,
      [name]: ['passingScore','questionCount'].includes(name) ? (isNaN(num) ? 0 : num) : value
    }));
  };

  const handleUpsert = async () => {
    if (!form.code || !form.name) {
      setError('Código e Nome são obrigatórios.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload: any = { 
        ...form, 
        subtitle, 
        logoUrl,
        questionIds: form.questionIds || [], 
        domains: form.domains || [] 
      };
      // 1. Salva o simulado
      await apiClient.post('/admin/simulados', payload);
      
      // 2. Atualiza a lista local imediatamente
      await loadSimulados();
      
      // Limpa o formulário e volta para o passo 1
      setForm({ ...emptyForm });
      setSubtitle('');
      setLogoUrl('');
      setStep(1);
      alert('Simulado salvo com sucesso!');
    } catch (err: any) {
      setError(err.message || 'Falha ao salvar simulado');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSiteSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const normalizedSiteLogoUrl = (siteLogoUrl || '').trim();
      const newCfg: SiteConfig = {
        ...currentConfig,
        pageTitle: pageTitle,
        mainTitle: mainTitle,
        siteLogoUrl: normalizedSiteLogoUrl,
        siteUrl: siteUrl, 
      };
      const updatedCfg = await apiClient.put('/settings', newCfg);
      onConfigChange(updatedCfg as SiteConfig);
      alert('Configurações do site atualizadas!');
    } catch (err: any) {
      setError(err.message || 'Falha ao atualizar configurações do site');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Gerenciamento de Simulados</h3>
      
      {error && <p className="text-red-500 mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded border border-red-100 dark:border-red-900/30">{error}</p>}

      {/* Seção 1: Configurações do Site (Globais) */}
      <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-slate-700 w-full">
        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <i className="fas fa-globe mr-2 text-blue-500"></i>
          Configurações do Site (Globais)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Título da Página (Aba)</label>
            <input value={pageTitle} onChange={(e) => setPageTitle(e.target.value)} placeholder="Ex: Simulados AWS" className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Título Principal (Topo)</label>
            <input value={mainTitle} onChange={(e) => setMainTitle(e.target.value)} placeholder="Ex: AWS Certified Solutions Architect" className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Link da Página (URL)</label>
            <input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://www.seusite.com" className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Logo do Site (URL)</label>
            <input value={siteLogoUrl} onChange={(e) => setSiteLogoUrl(e.target.value)} placeholder="https://.../site-logo.png" className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button 
            onClick={handleUpdateSiteSettings} 
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors shadow-md flex items-center gap-2"
          >
            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
            {loading ? 'Aplicando...' : 'Aplicar Configurações do Site'}
          </button>
        </div>
      </div>

      {/* Painel de Lista (Simulados Cadastrados) - Agora primeiro e largura total */}
      <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-slate-700 w-full">
        <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
          <i className="fas fa-list mr-2 text-teal-500"></i>
          Simulados Cadastrados
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {simulados.map((s) => (
            <div key={s.code} className="flex flex-col justify-between p-4 rounded-lg bg-gray-50 dark:bg-slate-700/50 border border-gray-100 dark:border-slate-600 hover:border-indigo-300 dark:hover:border-indigo-500 transition-colors">
              <div className="mb-3">
                <div className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  {s.logoUrl && <img src={s.logoUrl} className="w-6 h-6 object-contain" alt="" />}
                  {s.name} 
                  <span className="text-xs font-mono text-gray-400">({s.code})</span>
                </div>
                <div className="text-[10px] text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span><i className="fas fa-check mr-1"></i>{s.passingScore} pts</span>
                  <span><i className="fas fa-clock mr-1"></i>{Math.floor(s.examDurationSeconds/60)}m</span>
                  <span><i className="fas fa-database mr-1"></i>{s.questionCount} questões</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    setForm({ ...s });
                    setSubtitle(s.subtitle || '');
                    setLogoUrl(s.logoUrl || '');
                    loadQuestions(s.code);
                    setStep(1);
                    // Scroll suave até o formulário que agora está abaixo
                    const formElement = document.getElementById('simulado-form-container');
                    if (formElement) formElement.scrollIntoView({ behavior: 'smooth' });
                  }} 
                  className="flex-1 text-xs bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded transition-colors"
                >
                  Editar
                </button>
                <button onClick={async () => { if(window.confirm(`Excluir simulado ${s.name}?`)) { await apiClient.delete(`/admin/simulados/${s.code}`); await loadSimulados(); } }} className="flex-1 text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded transition-colors">Excluir</button>
              </div>
            </div>
          ))}
          {simulados.length === 0 && !loading && <div className="col-span-full text-center py-8 text-gray-500 dark:text-slate-400 italic">Nenhum simulado cadastrado.</div>}
          {loading && <div className="col-span-full text-center py-8 text-gray-500 animate-pulse">Carregando lista...</div>}
        </div>
      </div>

      {/* Painel de Formulário (Cadastrar/Editar) - Agora abaixo e largura total */}
      <div id="simulado-form-container" className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-slate-700 w-full">
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
            <i className="fas fa-plus-circle mr-2 text-orange-500"></i>
            {form.code ? 'Editar Simulado' : 'Novo Simulado'}
          </h4>
          <div className="flex items-center gap-4">
            {form.code && (
              <button 
                onClick={() => { setForm({...emptyForm}); setSubtitle(''); setLogoUrl(''); setStep(1); }}
                className="text-xs text-blue-600 hover:underline"
              >
                Criar Novo
              </button>
            )}
            <div className="flex gap-1">
              {[1, 2, 3].map(s => (
                <div key={s} className={`w-8 h-1 rounded-full ${step >= s ? 'bg-orange-500' : 'bg-gray-200 dark:bg-slate-700'}`}></div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {step === 1 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">Passo 1: Informações Básicas</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Código Identificador (único)</label>
                  <input 
                    name="code" 
                    value={form.code} 
                    onChange={handleChange} 
                    disabled={!!simulados.find(s => s.code === form.code && form.code !== '') && step > 1}
                    placeholder="ex.: saa-c03" 
                    className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white disabled:opacity-50" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Nome de Exibição</label>
                  <input name="name" value={form.name} onChange={handleChange} placeholder="AWS SAA-C03" className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Subtítulo / Descrição Curta</label>
                <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Descrição curta do simulado" className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">URL da Logo do Simulado</label>
                <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://.../simulado-logo.png" className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
              </div>

              <button 
                onClick={() => { if(form.code && form.name) setStep(2); else alert('Preencha código e nome primeiro'); }}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-lg transition-all"
              >
                Próximo: Importar Questões <i className="fas fa-arrow-right ml-2"></i>
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">Passo 2: Banco de Questões</p>
              
              <QuestionImportPanel targetSimCode={form.code} onImportComplete={() => loadQuestions(form.code)} />
              
              <div className="mt-6 border-t dark:border-slate-700 pt-6">
                 <div className="flex justify-between items-center mb-4">
                    <h5 className="text-sm font-bold flex items-center gap-2">
                      <i className="fas fa-database text-indigo-500"></i>
                      Questões no Simulado ({questions.length})
                    </h5>
                    <button 
                      onClick={() => { setEditingQuestion(null); setQuestionForm({ options: { A: '', B: '', C: '', D: '' }, correctAnswers: [], isMultiChoice: false, explanation: { general: '', options: {} } }); setShowQuestionModal(true); }}
                      className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
                    >
                      <i className="fas fa-plus"></i> Nova Questão
                    </button>
                 </div>

                 <div className="max-h-[500px] overflow-y-auto border rounded-lg dark:border-slate-700 bg-gray-50 dark:bg-slate-900/30">
                    {loadingQuestions ? (
                      <div className="p-8 text-center text-gray-500 animate-pulse">Carregando questões...</div>
                    ) : questions.map(q => (
                      <div key={q.id} className="p-3 border-b dark:border-slate-800 last:border-0 flex items-center justify-between hover:bg-white dark:hover:bg-slate-800 transition-colors">
                         <div className="flex-1 truncate pr-4">
                            <span className="font-mono text-[10px] bg-gray-200 dark:bg-slate-700 px-1.5 py-0.5 rounded mr-2">#{q.id}</span>
                            <span className="text-xs text-gray-700 dark:text-slate-300">{q.text}</span>
                         </div>
                         <div className="flex gap-2">
                            <button 
                              onClick={() => { setEditingQuestion(q); setQuestionForm(q); setShowQuestionModal(true); }}
                              className="p-1.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                              title="Editar"
                            >
                              <i className="fas fa-edit text-xs"></i>
                            </button>
                            <button 
                              onClick={() => { setEditingQuestion(q); setShowDeleteModal(true); }}
                              className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Excluir"
                            >
                              <i className="fas fa-trash text-xs"></i>
                            </button>
                         </div>
                      </div>
                    ))}
                    {questions.length === 0 && !loadingQuestions && (
                      <div className="p-8 text-center text-gray-400 italic text-sm">Nenhuma questão para este código ainda.</div>
                    )}
                 </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setStep(1)} className="flex-1 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-white font-bold py-3 rounded-lg">Voltar</button>
                <button onClick={() => setStep(3)} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-lg">Próximo: Finalizar <i className="fas fa-arrow-right ml-2"></i></button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-right-4">
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wider">Passo 3: Configurações de Exame</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Aprovação (Pts)</label>
                  <input name="passingScore" type="number" value={form.passingScore} onChange={handleChange} className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Duração (Min)</label>
                  <input name="examDurationMinutes" type="number" value={Math.floor(form.examDurationSeconds/60)} onChange={handleChange} className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Questões/Teste</label>
                  <input name="questionCount" type="number" value={form.questionCount || 0} onChange={handleChange} className="w-full p-2 rounded border dark:bg-slate-700 dark:border-slate-600" />
                </div>
              </div>

              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-900/30 rounded-lg">
                <p className="text-xs text-orange-700 dark:text-orange-400">
                  <i className="fas fa-info-circle mr-2"></i>
                  Ao salvar, o simulado ficará imediatamente disponível para ser atribuído aos usuários no painel de controle.
                </p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-white font-bold py-3 rounded-lg">Voltar</button>
                <button 
                  onClick={handleUpsert} 
                  disabled={loading} 
                  className="flex-grow-[2] bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg shadow-lg active:scale-95 transition-all"
                >
                  {loading ? 'Salvando...' : 'SALVAR E ATIVAR SIMULADO'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modais para CRUD de Questões */}
      <QuestionEditModal
          isOpen={showQuestionModal}
          onConfirm={handleSaveQuestion}
          onCancel={() => { setShowQuestionModal(false); setEditingQuestion(null); setQuestionForm({}); }}
          title={editingQuestion ? `Editar Questão ${editingQuestion.id}` : 'Adicionar Nova Questão'}
          confirmText="Salvar"
          confirmButtonClass="bg-blue-600 hover:bg-blue-700"
      >
          <div className="space-y-4">
              <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Link de Imagem (imgLink)</label>
                  <input type="text" name="imgLink" value={questionForm.imgLink || ''} onChange={handleQuestionFormChange} className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" />
                  {questionForm.imgLink && <div className="mt-3"><img src={questionForm.imgLink} alt="Preview" className="max-h-48 w-auto rounded border" /></div>}
              </div>
              <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Texto da Questão</label>
                  <textarea name="text" value={questionForm.text || ''} onChange={handleQuestionFormChange} rows={3} className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white resize-y" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                  {['A', 'B', 'C', 'D'].map(optionKey => (
                      <div key={optionKey}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Opção {optionKey}</label>
                          <input type="text" value={questionForm.options?.[optionKey] || ''} onChange={(e) => handleOptionsChange(optionKey, e.target.value)} className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" />
                      </div>
                  ))}
              </div>
              <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Respostas Corretas</label>
                      <div className="flex space-x-4">
                          {['A', 'B', 'C', 'D'].map(optionKey => (
                              <label key={optionKey} className="inline-flex items-center">
                                  <input type="checkbox" value={optionKey} checked={questionForm.correctAnswers?.includes(optionKey) || false} onChange={handleCorrectAnswersChange} className="form-checkbox h-5 w-5 text-orange-600" />
                                  <span className="ml-2 text-gray-700 dark:text-slate-300">{optionKey}</span>
                              </label>
                          ))}
                      </div>
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Múltipla Escolha?</label>
                      <input type="checkbox" name="isMultiChoice" checked={questionForm.isMultiChoice || false} onChange={(e) => setQuestionForm(prev => ({ ...prev, isMultiChoice: e.target.checked }))} className="form-checkbox h-5 w-5 text-orange-600" />
                  </div>
              </div>
              <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Explicação Geral</label>
                  <textarea name="explanation.general" value={questionForm.explanation?.general || ''} onChange={handleQuestionFormChange} rows={2} className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white resize-y" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                  <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Domínio</label>
                      <input type="text" name="domain" value={questionForm.domain || ''} onChange={handleQuestionFormChange} className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" />
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Link da Documentação</label>
                      <input type="text" name="docLink" value={questionForm.docLink || ''} onChange={handleQuestionFormChange} className="w-full px-3 py-2 border rounded-md dark:bg-slate-800 dark:border-slate-600 dark:text-white text-sm" />
                  </div>
              </div>
          </div>
      </QuestionEditModal>

      <ConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleDeleteQuestion}
          onCancel={() => { setShowDeleteModal(false); setEditingQuestion(null); }}
          title={`Excluir Questão ${editingQuestion?.id}?`}
          confirmText="Excluir"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
      >
          Tem certeza de que deseja excluir a questão {editingQuestion?.id}? Esta ação é irreversível.
      </ConfirmationModal>
    </div>
  );
};

export default SimuladoManagement;
